/**
 * Transmission (congestion) rent
 *
 * When the locational marginal prices (LMPs) at the two ends of a line differ,
 * the line is said to collect "transmission rent" (a.k.a. congestion rent or
 * merchandising surplus). Economically, the system operator buys power at the
 * cheap (low-LMP) end and sells it at the expensive (high-LMP) end; the spread,
 * multiplied by the megawatts flowing across the line, is the rent.
 *
 *   rent = flow_from_to  ×  (LMP_to − LMP_from)      [$/h]
 *
 * where `flow_from_to` is the real power flowing from the line's `from` bus to
 * its `to` bus (MW), and the LMPs are in $/MWh. Because power flows toward the
 * higher-priced bus, this quantity is non-negative in an optimal dispatch: if
 * flow is positive (from→to) then LMP_to ≥ LMP_from, and if flow reverses then
 * LMP_from ≥ LMP_to, so the product stays ≥ 0. Uncongested lines have equal
 * LMPs at both ends and therefore collect zero rent.
 *
 * Summing the rent over every line gives the total congestion rent for the
 * hour, which equals total payments by loads minus total payments to
 * generators (the operator's merchandising surplus).
 */

import { GridEdge } from '../types';

/**
 * Congestion rent for a single line, in $/h, for one dispatch snapshot (hour).
 *
 * `lmpByNodeId` maps a node id to its LMP ($/MWh). Returns `undefined` when
 * either endpoint has no LMP (e.g. the dispatch is infeasible or not yet
 * solved), so callers can distinguish "no rent" from "unknown".
 */
export function computeLineRent(
  line: GridEdge,
  lmpByNodeId: Map<string, number | undefined>
): number | undefined {
  const lmpFrom = lmpByNodeId.get(line.from);
  const lmpTo = lmpByNodeId.get(line.to);
  if (lmpFrom === undefined || lmpTo === undefined) {
    return undefined;
  }
  // `current` is the real power flow from the `from` bus to the `to` bus (MW).
  const flowFromTo = line.current ?? 0;
  return flowFromTo * (lmpTo - lmpFrom);
}

/**
 * Build a node-id → LMP lookup from a list of dispatched nodes. Convenience for
 * callers that have the hourly node array and want to price several lines.
 */
export function lmpLookup(
  nodes: Array<{ id: string; lmp?: number }>
): Map<string, number | undefined> {
  return new Map(nodes.map((n) => [n.id, n.lmp]));
}
